import { zodResolver } from '@hookform/resolvers/zod';
import { BracesIcon, PlusIcon, UserRoundIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { Incoming } from '@/components/brand/PhoneConversation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { VariableTextarea } from '@/components/VariableTextarea';
import { errorMessage } from '@/lib/api';
import { BUILTIN_VARIABLES, CONTACT_VARIABLES, KIND_LABEL, exampleValues, extractTemplateVariables, renderTemplate, variableLabel, type VariableInfo } from '@/lib/templates';
import type { MessageTemplateDto, MessageTemplateInput } from '@/lib/types';
import { cn } from '@/lib/utils';
import { VariableChip } from './TokenText';

const schema = z.object({
  name: z.string().trim().min(1, 'Give the template a name').max(120),
  category: z.string().trim().max(40),
  body: z.string().trim().min(1, 'Write the message').max(2000),
});
type Values = z.infer<typeof schema>;

const CATEGORY_SUGGESTIONS = ['Schedule', 'Site', 'Safety', 'Weather', 'Timecards', 'Reminders'];

/**
 * Create, edit or read a message template. Built-in and per-recipient variables insert with one
 * click at the caret; any other `{{token}}` you type becomes a fill-in question when the template
 * is used. The preview shows the text with sample values, the way one crew member would read it.
 * `readOnly` is for templates owned elsewhere (an organization template seen from a job), with
 * `editHref` pointing at the page that can change it.
 */
export function TemplateEditorDialog({
  open,
  onOpenChange,
  template,
  initial,
  onSave,
  busy,
  scopeLabel,
  readOnly,
  editHref,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  template?: MessageTemplateDto | null;
  /** Starting values for a new template (e.g. "Save as template" from Compose). Ignored when `template` is set. */
  initial?: { name?: string; category?: string; body?: string } | null;
  onSave: (body: MessageTemplateInput) => Promise<unknown>;
  busy?: boolean;
  scopeLabel: string;
  readOnly?: boolean;
  editHref?: string;
}) {
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { name: '', category: '', body: '' } });
  useEffect(() => {
    if (open) {
      setError(null);
      form.reset({ name: template?.name ?? initial?.name ?? '', category: template?.category ?? initial?.category ?? '', body: template?.body ?? initial?.body ?? '' });
    }
  }, [open, template, initial, form]);

  const body = form.watch('body');
  const vars = extractTemplateVariables(body);
  const custom = vars.filter((v) => v.kind === 'custom');
  const personal = vars.filter((v) => v.kind === 'contact');
  const preview = renderTemplate(body, exampleValues(vars));
  const chars = preview.length;

  const insert = (key: string) => {
    if (readOnly) return;
    const el = bodyRef.current;
    const token = `{{${key}}}`;
    const cur = form.getValues('body');
    if (!el) {
      form.setValue('body', `${cur}${token}`, { shouldDirty: true });
      return;
    }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const pad = start > 0 && !/\s$/.test(cur.slice(0, start)) ? ' ' : '';
    const next = `${cur.slice(0, start)}${pad}${token}${cur.slice(end)}`;
    form.setValue('body', next, { shouldDirty: true });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + pad.length + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const submit = form.handleSubmit(async (v) => {
    if (readOnly) return;
    setError(null);
    try {
      await onSave({ name: v.name, body: v.body, category: v.category || null });
      toast.success(template ? 'Template saved' : 'Template created');
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  const { ref: bodyFormRef, ...bodyField } = form.register('body');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-normal tracking-tight">{readOnly ? template?.name : template ? 'Edit template' : 'New template'}</DialogTitle>
          <DialogDescription>{scopeLabel}. Use variables for the parts that change; the rest stays exactly as you write it.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]" noValidate>
          <div className="grid gap-4">
            <FormError message={error} />
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px]">
              <Field label="Name" htmlFor="t-name" required error={form.formState.errors.name?.message}>
                <Input id="t-name" placeholder="Timecard reminder" autoFocus={!readOnly} readOnly={readOnly} {...form.register('name')} />
              </Field>
              <Field label="Category" htmlFor="t-category" error={form.formState.errors.category?.message}>
                <Input id="t-category" list="template-categories" placeholder="Schedule" readOnly={readOnly} {...form.register('category')} />
                <datalist id="template-categories">
                  {CATEGORY_SUGGESTIONS.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </Field>
            </div>
            <Field label="Message" htmlFor="t-body" required error={form.formState.errors.body?.message} description={readOnly ? undefined : 'Type {{name}} for something to fill in each time, e.g. {{gate}}. Add a fallback for empty fields: {{contact.firstName|there}}.'}>
              <VariableTextarea
                id="t-body"
                rows={6}
                placeholder="{{contact.firstName|Team}}, timecards for {{job.name}} are due by 3 PM {{day}}. Text your foreman with corrections."
                className="text-base leading-relaxed"
                maxLength={2000}
                readOnly={readOnly}
                value={body}
                {...bodyField}
                ref={(el) => {
                  bodyFormRef(el);
                  bodyRef.current = el;
                }}
              />
            </Field>
            {!readOnly && (
              <div className="grid gap-3">
                <InsertGroup icon={UserRoundIcon} label="Personalize for each recipient" hint="Filled from the contact record when it sends" items={CONTACT_VARIABLES} onInsert={insert} tone="contact" />
                <InsertGroup icon={BracesIcon} label="From the job and you" hint="Filled when the template is used" items={BUILTIN_VARIABLES} onInsert={insert} tone="builtin" />
              </div>
            )}
          </div>

          <div className="grid content-start gap-4">
            <div className="grid gap-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Preview{personal.length ? ' for one recipient' : ''}</span>
                {body ? <span className="text-muted-foreground text-xs tabular-nums">{chars} chars</span> : null}
              </div>
              <div className="bg-muted/40 border p-4">
                <Incoming>{preview || <span className="text-muted-foreground italic">Your message appears here.</span>}</Incoming>
              </div>
              {personal.length > 0 && <p className="text-muted-foreground text-xs">Shown with sample names. Each person gets their own text.</p>}
            </div>
            <div className="grid gap-2">
              <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Variables in this template</span>
              {vars.length === 0 ? (
                <p className="text-muted-foreground text-xs">None yet. Everything is sent as written.</p>
              ) : (
                <ul className="grid gap-1.5">
                  {vars.map((v) => (
                    <li key={v.key} className="flex items-center justify-between gap-2 text-xs">
                      <VariableChip kind={v.kind}>
                        {`{{${v.key}}}`}
                        {v.fallback ? <span className="opacity-70">| {v.fallback}</span> : null}
                      </VariableChip>
                      <Badge variant={v.kind === 'custom' ? 'secondary' : 'muted'} className={cn(v.kind === 'contact' && 'border-primary/40')}>
                        {KIND_LABEL[v.kind]}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
              {custom.length > 0 && <p className="text-muted-foreground text-xs">Whoever uses it types {custom.map((c) => variableLabel(c.key).toLowerCase()).join(', ')} first.</p>}
            </div>
          </div>

          <DialogFooter className="lg:col-span-2">
            {readOnly ? (
              <>
                {editHref && (
                  <Button asChild variant="outline">
                    <Link to={editHref}>Edit under Organization → Templates</Link>
                  </Button>
                )}
                <Button type="button" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy}>
                  {busy ? 'Saving…' : template ? 'Save changes' : 'Create template'}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InsertGroup({ icon: Icon, label, hint, items, onInsert, tone }: { icon: typeof BracesIcon; label: string; hint: string; items: ReadonlyArray<VariableInfo>; onInsert: (key: string) => void; tone: 'contact' | 'builtin' }) {
  return (
    <div className="grid gap-1.5">
      <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
        <Icon className="size-3.5" aria-hidden /> <span className="font-medium">{label}</span> <span aria-hidden>·</span> {hint}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {items.map((v) => (
          <button key={v.key} type="button" onClick={() => onInsert(v.key)} className={cn('flex h-7 items-center gap-1 border px-2 text-xs transition-colors', tone === 'contact' ? 'border-primary/40 bg-accent hover:border-primary' : 'bg-card hover:border-foreground/30')} title={`${v.label}, e.g. ${v.example}`}>
            <PlusIcon className="size-3 opacity-60" aria-hidden />
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}
